import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Breadcrumbs,
  Link as MUILink,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useAuth } from '../../components/features/auth/AuthGuard';
import { PERMISSIONS } from '../../constants';
import useNotification from '../../hooks/common/useNotification';
import { LoadingSpinner } from '../../components/common/Loading';
import { performanceAPI } from '../../services/api/performance.api';
import usersAPI from '../../services/api/users.api';

const CreateAppraisalPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { showSuccess, showError } = useNotification();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [filteredObjectives, setFilteredObjectives] = useState([]);

  const [formData, setFormData] = useState({
    user_id: '',
    objective_id: '',
  });

  const [errors, setErrors] = useState({});

  // Check permissions
  if (!hasPermission(PERMISSIONS.PERFORMANCE_CREATE) && !hasPermission(PERMISSIONS.CREATE_PERFORMANCE_APPRAISALS)) {
    return (
      <Box p={3}>
        <Alert severity="error">
          You don't have permission to create performance appraisals.
        </Alert>
      </Box>
    );
  }

  useEffect(() => {
    fetchEmployees();
    fetchObjectives();
  }, []);

  useEffect(() => {
    // Filter objectives based on selected user
    if (formData.user_id) {
      const userObjectives = objectives.filter(
        (obj) => obj.user_id === parseInt(formData.user_id) && obj.is_approved
      );
      setFilteredObjectives(userObjectives);
    } else {
      setFilteredObjectives([]);
    }
  }, [formData.user_id, objectives]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getAllUsers({ limit: 1000, status: 'active' });
      if (response.data.success) {
        setEmployees(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      showError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchObjectives = async () => {
    try {
      const response = await performanceAPI.getAllObjectives({ limit: 1000, is_approved: true });
      if (response.data.success) {
        setObjectives(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching objectives:', error);
      showError('Failed to load objectives');
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear objective when user changes
    if (field === 'user_id') {
      setFormData((prev) => ({
        ...prev,
        objective_id: '',
      }));
    }

    // Clear errors
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.user_id) {
      newErrors.user_id = 'Please select an employee';
    }

    if (!formData.objective_id) {
      newErrors.objective_id = 'Please select an objective';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      const response = await performanceAPI.createAppraisal({
        user_id: parseInt(formData.user_id),
        objective_id: parseInt(formData.objective_id),
      });

      if (response.data.success) {
        showSuccess('Appraisal created successfully');
        navigate('/performance/appraisals');
      } else {
        showError(response.data.message || 'Failed to create appraisal');
      }
    } catch (error) {
      console.error('Error creating appraisal:', error);
      if (error.response?.status === 409) {
        showError('An appraisal already exists for this employee and objective');
      } else if (error.response?.status === 400) {
        showError(error.response.data.message || 'Invalid data provided');
      } else {
        showError('Failed to create appraisal. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const selectedEmployee = employees.find((emp) => emp.id === parseInt(formData.user_id));
  const selectedObjective = objectives.find((obj) => obj.id === parseInt(formData.objective_id));

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box p={3}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <MUILink
          component="button"
          variant="body1"
          onClick={() => navigate('/performance/appraisals')}
          sx={{ cursor: 'pointer' }}
        >
          Appraisals
        </MUILink>
        <Typography color="text.primary">Create Appraisal</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/performance/appraisals')}
        >
          Back
        </Button>
        <Typography variant="h4">Create New Appraisal</Typography>
      </Box>

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Employee Selection */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.user_id}>
                  <InputLabel>Employee *</InputLabel>
                  <Select
                    value={formData.user_id}
                    onChange={handleChange('user_id')}
                    label="Employee *"
                  >
                    {employees.map((employee) => (
                      <MenuItem key={employee.id} value={employee.id}>
                        {employee.first_name} {employee.middle_name} {employee.sur_name}
                        {employee.email && ` (${employee.email})`}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.user_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {errors.user_id}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Objective Selection */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.objective_id} disabled={!formData.user_id}>
                  <InputLabel>Objective *</InputLabel>
                  <Select
                    value={formData.objective_id}
                    onChange={handleChange('objective_id')}
                    label="Objective *"
                    disabled={!formData.user_id}
                  >
                    {filteredObjectives.length === 0 && formData.user_id ? (
                      <MenuItem disabled>
                        No approved objectives found for this employee
                      </MenuItem>
                    ) : (
                      filteredObjectives.map((objective) => (
                        <MenuItem key={objective.id} value={objective.id}>
                          {objective.title}
                          {objective.implementation_quarter && ` (${objective.implementation_quarter})`}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {errors.objective_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {errors.objective_id}
                    </Typography>
                  )}
                  {formData.user_id && filteredObjectives.length === 0 && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      This employee has no approved objectives. Please create and approve an objective first.
                    </Alert>
                  )}
                </FormControl>
              </Grid>

              {/* Selected Objective Details */}
              {selectedObjective && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        Objective Details:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Title:</strong> {selectedObjective.title}
                      </Typography>
                      {selectedObjective.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          <strong>Description:</strong> {selectedObjective.description}
                        </Typography>
                      )}
                      {selectedObjective.implementation_quarter && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          <strong>Quarter:</strong> {selectedObjective.implementation_quarter}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/performance/appraisals')}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={submitting || !formData.user_id || !formData.objective_id}
                  >
                    {submitting ? 'Creating...' : 'Create Appraisal'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreateAppraisalPage;

